let jsZipModulePromise;
let mammothModulePromise;
let pdfJsModulePromise;

const loadJsZip = async () => {
  if (!jsZipModulePromise) {
    jsZipModulePromise = import("jszip").then((module) => module.default || module);
  }
  return jsZipModulePromise;
};

const loadMammoth = async () => {
  if (!mammothModulePromise) {
    mammothModulePromise = import("mammoth").then((module) => module.default || module);
  }
  return mammothModulePromise;
};

const loadPdfJs = async () => {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise = import("pdfjs-dist").then((module) => {
      const pdfJs = module.default || module;
      pdfJs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
      ).toString();
      return pdfJs;
    });
  }
  return pdfJsModulePromise;
};

/**
 * Parse Canvas filename to extract student metadata
 * Pattern: {lastname}{firstname}_{studentid}_{submissionid}_{original_filename}.{extension}
 * Example: "pashovaalexandra_2894_1336920_Copy of Reading Guide.docx.pdf"
 *
 * @param {string} filename - Canvas export filename
 * @returns {Object} Parsed metadata { lastName, firstName, studentId, submissionId, originalName, extension, fullName }
 */
export function parseCanvasFilename(filename) {
  try {
    const baseName = filename.split('/').pop() || filename;
    // Flexible pattern: name_studentId_optionalSubmissionId_optionalRest
    // Examples:
    //   pashovaalexandra_2894_1336920_Copy of Reading Guide.docx.pdf
    //   wolfeashlyn_2926_link.html
    //   clausenayden_288_link.html
    //   leetrevin_3700_link.html
    const match = baseName.match(/^([^_]+)_([^_]+)_?(.*)$/);
    if (!match) {
      throw new Error('Invalid Canvas filename format');
    }

    const namePart = match[1];
    const studentId = match[2];
    const remainder = match[3] || '';

    // If a submission id is present, it should be the next underscore chunk
    const remainderParts = remainder.split('_');
    const submissionId =
      remainderParts.length > 1
        ? remainderParts[0]
        : remainderParts[0] || 'unknown';
    const originalName =
      remainderParts.length > 1
        ? remainderParts.slice(1).join('_')
        : remainder || baseName;

    // Extract extension
    const extensionMatch = originalName.match(/\.([^.]+)$/);
    const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';

    // Try to parse name (this is tricky without a delimiter)
    // We'll use a heuristic: look for capital letters or common name patterns
    let lastName = '';
    let firstName = '';

    // Simple heuristic: split on first uppercase letter after lowercase
    const nameMatch = namePart.match(/^([a-z]+)([a-z]+)$/i);
    if (nameMatch) {
      // If all lowercase, try to split roughly in half
      const midpoint = Math.floor(namePart.length / 2);
      lastName = namePart.substring(0, midpoint);
      firstName = namePart.substring(midpoint);
    } else {
      // Fallback: just use the whole name part
      lastName = namePart;
      firstName = '';
    }

    const fullName = `${firstName} ${lastName}`.trim();

    return {
      lastName: lastName.charAt(0).toUpperCase() + lastName.slice(1),
      firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
      studentId,
      submissionId,
      originalName,
      extension,
      fullName: fullName.charAt(0).toUpperCase() + fullName.slice(1),
      rawNamePart: namePart
    };
  } catch (error) {
    console.error('Error parsing Canvas filename:', filename, error);
    return null;
  }
}

/**
 * Extract text content from DOCX file
 *
 * @param {Blob} fileBlob - DOCX file blob
 * @returns {Promise<string>} Extracted text content
 */
export async function extractTextFromDocx(fileBlob) {
  try {
    const mammoth = await loadMammoth();
    const arrayBuffer = await fileBlob.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value || '';
  } catch (error) {
    console.error('Error extracting DOCX text:', error);
    throw new Error(`Failed to extract DOCX content: ${error.message}`);
  }
}

/**
 * Extract text content from PDF file
 *
 * @param {Blob} fileBlob - PDF file blob
 * @returns {Promise<string>} Extracted text content
 */
export async function extractTextFromPdf(fileBlob) {
  try {
    const { getDocument } = await loadPdfJs();
    const arrayBuffer = await fileBlob.arrayBuffer();
    const pdf = await getDocument({ data: arrayBuffer }).promise;

    let fullText = '';

    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n\n';
    }

    return fullText.trim();
  } catch (error) {
    console.error('Error extracting PDF text:', error);
    throw new Error(`Failed to extract PDF content: ${error.message}`);
  }
}

/**
 * Extract GitHub repository URL from Canvas HTML link file
 *
 * @param {string} htmlContent - HTML content from link file
 * @returns {string|null} GitHub repo URL or null if not found
 */
export function extractGithubUrl(htmlContent) {
  try {
    const metaMatch = htmlContent.match(
      /<meta[^>]*http-equiv=["']refresh["'][^>]*content=["'](\d+);url=([^"']+)["']/i
    );

    if (metaMatch && metaMatch[2] && metaMatch[2].includes('github.com')) {
      return normalizeGithubUrl(metaMatch[2]);
    }

    const hrefMatch = htmlContent.match(/href=["']([^"']*github\.com[^"']*)["']/i);
    if (hrefMatch && hrefMatch[1]) {
      return normalizeGithubUrl(hrefMatch[1]);
    }

    const rawMatch = htmlContent.match(/https?:\/\/github\.com\/[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+/i);
    if (rawMatch && rawMatch[0]) {
      return normalizeGithubUrl(rawMatch[0]);
    }

    return null;
  } catch (error) {
    console.error('Error extracting GitHub URL:', error);
    return null;
  }
}

function normalizeGithubUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.toLowerCase().includes('github.com')) return null;
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length < 2) return null;
    const owner = parts[0];
    let repo = parts[1].replace(/\.git$/i, '');
    return `https://github.com/${owner}/${repo}`;
  } catch (error) {
    return null;
  }
}

/**
 * Extract Google Docs URL from Canvas HTML link file
 *
 * @param {string} htmlContent - HTML content from link file
 * @returns {string|null} Google Docs URL or null if not found
 */
export function extractGoogleDocsUrl(htmlContent) {
  try {
    // Parse meta refresh tag
    const metaMatch = htmlContent.match(
      /<meta[^>]*http-equiv=["']refresh["'][^>]*content=["'](\d+);url=([^"']+)["']/i
    );

    if (metaMatch && metaMatch[2]) {
      const url = metaMatch[2];

      // Validate it's a Google Docs URL
      if (url.includes('docs.google.com')) {
        return url;
      }
    }

    // Fallback: try to find any href with google docs
    const hrefMatch = htmlContent.match(/href=["']([^"']*docs\.google\.com[^"']*)["']/i);
    if (hrefMatch && hrefMatch[1]) {
      return hrefMatch[1];
    }

    return null;
  } catch (error) {
    console.error('Error extracting Google Docs URL:', error);
    return null;
  }
}

/**
 * Process entire Canvas ZIP file
 * Extracts all files, parses metadata, and extracts content
 *
 * @param {File} zipFile - Canvas ZIP file
 * @param {Function} onProgress - Progress callback (current, total, message)
 * @returns {Promise<Object>} { students: [], submissions: [], errors: [] }
 */
export async function processCanvasZip(zipFile, onProgress = () => {}) {
  const results = {
    students: [],
    submissions: [],
    errors: []
  };

  try {
    // Validate file size (100MB max)
    const MAX_SIZE = 100 * 1024 * 1024;
    if (zipFile.size > MAX_SIZE) {
      throw new Error('ZIP file too large (max 100MB)');
    }

    // Load ZIP file
    onProgress(0, 1, 'Reading ZIP file...');
    const JSZip = await loadJsZip();
    const zip = await JSZip.loadAsync(zipFile);

    // Get all files (excluding directories)
    const fileEntries = [];
    zip.forEach((relativePath, file) => {
      if (!file.dir) {
        fileEntries.push({ relativePath, file });
      }
    });

    const total = fileEntries.length;
    onProgress(0, total, `Found ${total} files`);

    // Track students by studentId to avoid duplicates
    const studentMap = new Map();

    // Process files in batches to avoid memory issues
    const BATCH_SIZE = 3;

    for (let i = 0; i < fileEntries.length; i += BATCH_SIZE) {
      const batch = fileEntries.slice(i, i + BATCH_SIZE);

      await Promise.all(batch.map(async ({ relativePath, file }) => {
        try {
          onProgress(i + 1, total, `Processing: ${relativePath}`);

          // Parse filename
          const metadata = parseCanvasFilename(relativePath);

          if (!metadata || !metadata.studentId) {
            results.errors.push({
              filename: relativePath,
              error: 'Invalid filename format - could not extract student ID'
            });
            return;
          }

          // Get file blob
          const blob = await file.async('blob');
          const extension = metadata.extension;

          let content = '';
          let repositoryUrl = null;

          // Extract content based on file type
          if (extension === 'docx') {
            try {
              content = await extractTextFromDocx(blob);
            } catch (error) {
              results.errors.push({
                filename: relativePath,
                error: `DOCX extraction failed: ${error.message}`,
                studentName: metadata.fullName
              });
              return;
            }
          } else if (extension === 'pdf') {
            try {
              content = await extractTextFromPdf(blob);
            } catch (error) {
              results.errors.push({
                filename: relativePath,
                error: `PDF extraction failed: ${error.message}`,
                studentName: metadata.fullName
              });
              return;
            }
          } else if (extension === 'html') {
            try {
              const htmlContent = await file.async('text');
              const githubUrl = extractGithubUrl(htmlContent);
              const url = extractGoogleDocsUrl(htmlContent);

              if (githubUrl) {
                content = `GitHub Repo: ${githubUrl}`;
                repositoryUrl = githubUrl;
              } else if (url) {
                content = `Google Doc: ${url}`;
              } else {
                results.errors.push({
                  filename: relativePath,
                  error: 'Could not extract GitHub or Google Docs URL from HTML file',
                  studentName: metadata.fullName
                });
                return;
              }
            } catch (error) {
              results.errors.push({
                filename: relativePath,
                error: `HTML parsing failed: ${error.message}`,
                studentName: metadata.fullName
              });
              return;
            }
          } else {
            results.errors.push({
              filename: relativePath,
              error: `Unsupported file type: .${extension}`,
              studentName: metadata.fullName
            });
            return;
          }

          // Check if content is empty
          if (!content || content.trim().length === 0) {
            results.errors.push({
              filename: relativePath,
              error: 'Extracted content is empty',
              studentName: metadata.fullName
            });
            return;
          }

          // Add/update student in map
          if (!studentMap.has(metadata.studentId)) {
            studentMap.set(metadata.studentId, {
              studentId: metadata.studentId,
              name: metadata.fullName,
              rawNamePart: metadata.rawNamePart,
              lastName: metadata.lastName,
              firstName: metadata.firstName,
              // Email will be generated or matched later
              email: null
            });
          }

          // Add submission
          results.submissions.push({
            studentId: metadata.studentId,
            studentName: metadata.fullName,
            content: content,
            repositoryUrl: repositoryUrl || null,
            originalFilename: metadata.originalName,
            fileType: extension,
            submissionId: metadata.submissionId
          });

        } catch (error) {
          console.error(`Error processing file ${relativePath}:`, error);
          results.errors.push({
            filename: relativePath,
            error: error.message || 'Unknown error during processing'
          });
        }
      }));
    }

    // Convert student map to array
    results.students = Array.from(studentMap.values());

    onProgress(total, total, `Completed! ${results.submissions.length} submissions, ${results.students.length} students`);

    return results;

  } catch (error) {
    console.error('Error processing Canvas ZIP:', error);
    throw error;
  }
}

/**
 * Generate email for student if not found in existing roster
 *
 * @param {string} studentId - Canvas student ID
 * @returns {string} Generated email address
 */
export function generateStudentEmail(studentId) {
  return `${studentId}@canvas.import`;
}

/**
 * Match student to existing roster by name or student ID
 *
 * @param {Object} canvasStudent - Student from Canvas export
 * @param {Array} existingStudents - Existing students from Firestore
 * @returns {Object|null} Matched student or null
 */
export function matchStudent(canvasStudent, existingStudents) {
  // Try to match by exact name (case-insensitive)
  const nameMatch = existingStudents.find(s =>
    s.name.toLowerCase() === canvasStudent.name.toLowerCase()
  );

  if (nameMatch) {
    return nameMatch;
  }

  // Try to match by raw name part (in case Canvas format matches)
  if (canvasStudent.rawNamePart) {
    const rawMatch = existingStudents.find(s => {
      const existingRaw = s.name.toLowerCase().replace(/\s+/g, '');
      return existingRaw === canvasStudent.rawNamePart.toLowerCase();
    });

    if (rawMatch) {
      return rawMatch;
    }
  }

  // No match found
  return null;
}
